import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ShieldAlert, RefreshCw, Search, Filter, CheckCircle2, XCircle, Globe, Clock } from 'lucide-react';

const REASON_LABELS = {
  ok: 'Accesso riuscito',
  credenziali_non_valide: 'Credenziali non valide',
  account_in_attesa: 'Account in attesa',
  account_disabilitato: 'Account disabilitato',
};

export default function LoginAttempts() {
  const [items, setItems] = useState([]);
  const [aggEmail, setAggEmail] = useState([]);
  const [aggIp, setAggIp] = useState([]);
  const [loading, setLoading] = useState(true);
  const [emailFilter, setEmailFilter] = useState('');
  const [ipFilter, setIpFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all|success|fail
  const [days, setDays] = useState('30');

  const load = async () => {
    setLoading(true);
    try {
      const params = { days: parseInt(days, 10) || 30, limit: 300 };
      if (emailFilter.trim()) params.email = emailFilter.trim();
      if (ipFilter.trim()) params.ip = ipFilter.trim();
      if (statusFilter === 'success') params.success = true;
      if (statusFilter === 'fail') params.success = false;
      const r = await api.listLoginAttempts(params);
      setItems(r.items || []);
      setAggEmail(r.fails_by_email || []);
      setAggIp(r.fails_by_ip || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Impossibile caricare i tentativi di accesso');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const totalSuccess = items.filter(i => i.success).length;
  const totalFail = items.filter(i => !i.success).length;

  return (
    <div>
      <PageHeader
        title="Tentativi di Accesso"
        description="Registro dei login riusciti e falliti per la sicurezza operativa. Visibile solo al Super Admin."
      />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        <Card data-testid="login-attempts-total">
          <CardContent className="p-4">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">Totale nel periodo</div>
            <div className="text-3xl font-semibold tabular-nums mt-1">{items.length}</div>
          </CardContent>
        </Card>
        <Card className="border-emerald-500/30" data-testid="login-attempts-success">
          <CardContent className="p-4">
            <div className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1"><CheckCircle2 className="size-3 text-emerald-500" />Successi</div>
            <div className="text-3xl font-semibold tabular-nums mt-1 text-emerald-600 dark:text-emerald-400">{totalSuccess}</div>
          </CardContent>
        </Card>
        <Card className="border-destructive/30" data-testid="login-attempts-fail">
          <CardContent className="p-4">
            <div className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1"><XCircle className="size-3 text-destructive" />Fallimenti</div>
            <div className="text-3xl font-semibold tabular-nums mt-1 text-destructive">{totalFail}</div>
          </CardContent>
        </Card>
      </div>

      <Card className="mb-4">
        <CardContent className="p-4 flex flex-col md:flex-row md:items-end gap-3 flex-wrap">
          <div className="space-y-1 min-w-[200px] flex-1">
            <Label className="text-xs">Email</Label>
            <div className="relative"><Search className="absolute left-2 top-2.5 size-3.5 text-muted-foreground" /><Input value={emailFilter} onChange={(e) => setEmailFilter(e.target.value)} placeholder="contiene..." className="pl-7" data-testid="login-attempts-filter-email" /></div>
          </div>
          <div className="space-y-1 min-w-[160px]">
            <Label className="text-xs">IP</Label>
            <div className="relative"><Globe className="absolute left-2 top-2.5 size-3.5 text-muted-foreground" /><Input value={ipFilter} onChange={(e) => setIpFilter(e.target.value)} placeholder="es. 1.2.3.4" className="pl-7" data-testid="login-attempts-filter-ip" /></div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Esito</Label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[160px]" data-testid="login-attempts-filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti</SelectItem>
                <SelectItem value="success">Solo successi</SelectItem>
                <SelectItem value="fail">Solo fallimenti</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Ultimi giorni</Label>
            <Select value={days} onValueChange={setDays}>
              <SelectTrigger className="w-[120px]" data-testid="login-attempts-filter-days"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="1">1</SelectItem>
                <SelectItem value="7">7</SelectItem>
                <SelectItem value="30">30</SelectItem>
                <SelectItem value="90">90</SelectItem>
                <SelectItem value="365">365</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-2 items-end">
            <Button onClick={load} disabled={loading} data-testid="login-attempts-apply-filters">
              <Filter className="size-3.5 mr-1" />Applica
            </Button>
            <Button variant="outline" size="icon" onClick={load} disabled={loading} title="Ricarica" data-testid="login-attempts-refresh">
              <RefreshCw className={`size-3.5 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardContent>
      </Card>

      {(aggEmail.length > 0 || aggIp.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
          {aggEmail.length > 0 && (
            <Card data-testid="login-attempts-agg-email">
              <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><ShieldAlert className="size-4 text-destructive" />Email con più fallimenti</CardTitle></CardHeader>
              <CardContent className="space-y-1.5">
                {aggEmail.slice(0, 5).map((r) => (
                  <div key={r.email} className="flex items-center justify-between text-sm border-l-2 border-destructive/50 pl-2">
                    <span className="font-mono truncate max-w-[240px]" title={r.email}>{r.email}</span>
                    <Badge variant="destructive">{r.count} fallimenti</Badge>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
          {aggIp.length > 0 && (
            <Card data-testid="login-attempts-agg-ip">
              <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Globe className="size-4 text-destructive" />IP con più fallimenti</CardTitle></CardHeader>
              <CardContent className="space-y-1.5">
                {aggIp.slice(0, 5).map((r) => (
                  <div key={r.ip} className="flex items-center justify-between text-sm border-l-2 border-destructive/50 pl-2">
                    <span className="font-mono">{r.ip || '—'}</span>
                    <Badge variant="destructive">{r.count} fallimenti</Badge>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      <Card>
        <CardContent className="p-0">
          <div className="rounded-lg border overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Data e ora</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>IP</TableHead>
                <TableHead>Esito</TableHead>
                <TableHead>Motivo</TableHead>
                <TableHead>Ricordami</TableHead>
                <TableHead>User Agent</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {items.length === 0 && !loading && (
                  <TableRow><TableCell colSpan={7} className="text-center text-sm text-muted-foreground py-8">Nessun tentativo di accesso nel periodo selezionato.</TableCell></TableRow>
                )}
                {items.map((it) => (
                  <TableRow key={it.id} data-testid={`login-attempt-row-${it.id}`}>
                    <TableCell className="text-xs tabular-nums whitespace-nowrap"><Clock className="size-3 inline mr-1 text-muted-foreground" />{(it.created_at || '').slice(0, 19).replace('T', ' ')}</TableCell>
                    <TableCell className="font-mono text-xs max-w-[220px] truncate" title={it.email}>{it.email}</TableCell>
                    <TableCell className="font-mono text-xs">{it.ip || '—'}</TableCell>
                    <TableCell>
                      {it.success
                        ? <Badge variant="secondary" className="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"><CheckCircle2 className="size-3 mr-1" />Successo</Badge>
                        : <Badge variant="destructive"><XCircle className="size-3 mr-1" />Fallito</Badge>}
                    </TableCell>
                    <TableCell className="text-xs">{REASON_LABELS[it.reason] || it.reason || '—'}</TableCell>
                    <TableCell className="text-xs">{it.remember_me ? <Badge variant="outline">30 gg</Badge> : <span className="text-muted-foreground">7 gg</span>}</TableCell>
                    <TableCell className="text-[11px] text-muted-foreground max-w-[260px] truncate" title={it.user_agent}>{it.user_agent || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
